import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Chip,
    Button,
    IconButton,
    TextField,
    MenuItem,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Pagination,
    Skeleton,
    Avatar
} from '@mui/material';
import {  FaEye, FaEdit } from 'react-icons/fa';
import { FaWarehouse } from "react-icons/fa6";
import { MdDeleteForever, MdSearch, MdCheckCircle, MdPauseCircle, MdArrowBack, MdPerson, MdPlace } from "react-icons/md";
import { getData, postData, deleteData, putData } from '../../Webservice/Webservice';
import MapPicker from '../../components/MapPicker';
import WAREHOUSE_ROUTES from '../../API_Route/WarehouseRoute';
import { indianStates } from '../../Utils/states';

const PAGE_SIZE = 10;

const FormCard = ({ title, subtitle, accent, icon: Icon, children }) => (
    <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, boxShadow: '0 4px 20px rgba(15,23,42,.04)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
            <Box sx={{ background: accent + '1a', color: accent, borderRadius: '10px', p: 1, display: 'flex' }}><Icon size={18} /></Box>
            <Box>
                <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800, fontSize: 14.5, color: '#0f172a', lineHeight: 1.2 }}>{title}</Typography>
                <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: 11.5, color: '#64748b' }}>{subtitle}</Typography>
            </Box>
        </Box>
        {children}
    </Paper>
);

const Warehouse = () => {
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [warehouseForm, setWarehouseForm] = useState({
        name: '',
        address: '',
        city: '',
        state: '',
        country: '',
        pincode: '',
        latitude: '',
        longitude: '',
        contactPerson: '',
        phoneNumber: '',
        email: '',
        status: 'active'
    });
    const [formMode, setFormMode] = useState('edit');
    const isEditModeRef = useRef(false);
    const editWarehouseIdRef = useRef(null);
    const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
    const [warehouses, setWarehouses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [reloadKey, setReloadKey] = useState(0);

    const changeStatus = (value) => {
        setStatus(value);
        setMeta((prev) => ({ ...prev, page: 1 }));
    };
    // wait for the user to stop typing before searching
    useEffect(() => {
        const t = setTimeout(() => {
            setSearch(searchInput.trim());
            setMeta((prev) => ({ ...prev, page: 1 }));
        }, 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    // Validation state
    const [errors, setErrors] = useState({});

    // Pagination handler
    const handlePageChange = (event, newPage) => {
        setMeta((prev) => ({ ...prev, page: newPage }));
    };
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [warehouseToDelete, setWarehouseToDelete] = useState(null);

    const handleDeleteClick = (warehouse, e) => {
        e.stopPropagation();
        setWarehouseToDelete(warehouse);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setWarehouseToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!warehouseToDelete) return;
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            await deleteData(`warehouse/delete/${warehouseToDelete._id || warehouseToDelete.id}`, token);
            setReloadKey((k) => k + 1);
        } catch (error) {
            console.error('Error deleting warehouse:', error);
        } finally {
            handleCloseDeleteDialog();
        }
    };

    const handleShowCreateForm = () => {
        setShowCreateForm(true);
        isEditModeRef.current = false;
        setFormMode('edit');
        setWarehouseForm({
            name: '',
            address: '',
            city: '',
            state: '',
            country: '',
            pincode: '',
            latitude: '',
            longitude: '',
            contactPerson: '',
            phoneNumber: '',
            email: '',
            status: 'active'
        });
        editWarehouseIdRef.current = null;
        setErrors({});
    };

    const handleHideCreateForm = () => {
        setShowCreateForm(false);
        isEditModeRef.current = false;
        editWarehouseIdRef.current = null;
        setErrors({});
    };

    const handleEditWarehouse = (warehouse) => {
        setShowCreateForm(true);
        isEditModeRef.current = true;
        setFormMode('view');
        editWarehouseIdRef.current = warehouse._id || warehouse.id;
        setWarehouseForm({
            name: warehouse.name || '',
            address: warehouse.address || '',
            city: warehouse.city || '',
            state: warehouse.state || '',
            country: warehouse.country || '',
            pincode: warehouse.pincode || '',
            latitude: warehouse.latitude || '',
            longitude: warehouse.longitude || '',
            contactPerson: warehouse.contactPerson || '',
            phoneNumber: warehouse.phoneNumber || '',
            email: warehouse.email || '',
            status: warehouse.status || 'active'
        });
        setErrors({});
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setWarehouseForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Validation logic
    const validateForm = () => {
        const newErrors = {};

        // Name
        if (!warehouseForm.name || !warehouseForm.name.toString().trim()) {
            newErrors.name = 'Warehouse name is required';
        }

        // Contact person
        if (!warehouseForm.contactPerson || !warehouseForm.contactPerson.toString().trim()) {
            newErrors.contactPerson = 'Contact person is required';
        }

        // Phone - basic 10 digit check
        const rawPhone = (warehouseForm.phoneNumber || '').toString();
        const cleanedPhone = rawPhone.replace(/\D/g, '');
        if (!cleanedPhone) {
            newErrors.phoneNumber = 'Phone number is required';
        } else if (!/^\d{10}$/.test(cleanedPhone)) {
            newErrors.phoneNumber = 'Enter a valid 10-digit phone number';
        }

        // Email
        if (!warehouseForm.email || !warehouseForm.email.toString().trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^\S+@\S+\.\S+$/.test(warehouseForm.email)) {
            newErrors.email = 'Invalid email format';
        }

        // Address fields
        if (!warehouseForm.address || !warehouseForm.address.toString().trim()) {
            newErrors.address = 'Address is required';
        }
        if (!warehouseForm.city || !warehouseForm.city.toString().trim()) {
            newErrors.city = 'City is required';
        }
        if (!warehouseForm.state || !warehouseForm.state.toString().trim()) {
            newErrors.state = 'State is required';
        }
        if (!warehouseForm.country || !warehouseForm.country.toString().trim()) {
            newErrors.country = 'Country is required';
        }
        if (!warehouseForm.pincode || !warehouseForm.pincode.toString().trim()) {
            newErrors.pincode = 'Pincode is required';
        } else {
            const p = warehouseForm.pincode.toString().trim();
            if (!/^\d{4,6}$/.test(p)) {
                newErrors.pincode = 'Enter a valid pincode (4 to 6 digits)';
            }
        }

        // Latitude & Longitude (optional but if one present require both)
        const lat = warehouseForm.latitude?.toString()?.trim();
        const lng = warehouseForm.longitude?.toString()?.trim();
        if ((lat && !lng) || (!lat && lng)) {
            newErrors.latitude = 'Both latitude and longitude must be present';
            newErrors.longitude = 'Both latitude and longitude must be present';
        } else if (lat && lng) {
            if (isNaN(Number(lat)) || Number(lat) < -90 || Number(lat) > 90) {
                newErrors.latitude = 'Latitude must be a valid number between -90 and 90';
            }
            if (isNaN(Number(lng)) || Number(lng) < -180 || Number(lng) > 180) {
                newErrors.longitude = 'Longitude must be a valid number between -180 and 180';
            }
        }

        // Status
        if (!warehouseForm.status) {
            newErrors.status = 'Status is required';
        } else if (!['active', 'inactive'].includes(warehouseForm.status)) {
            newErrors.status = 'Invalid status';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Clear errors live when user corrects fields
    useEffect(() => {
        setErrors(prev => {
            const next = { ...prev };

            if (next.name && warehouseForm.name && warehouseForm.name.toString().trim()) delete next.name;
            if (next.contactPerson && warehouseForm.contactPerson && warehouseForm.contactPerson.toString().trim()) delete next.contactPerson;

            const rawPhone = (warehouseForm.phoneNumber || '').toString();
            const cleanedPhone = rawPhone.replace(/\D/g, '');
            if (next.phoneNumber && cleanedPhone && /^\d{10}$/.test(cleanedPhone)) delete next.phoneNumber;

            if (next.email && warehouseForm.email && /^\S+@\S+\.\S+$/.test(warehouseForm.email)) delete next.email;

            if (next.address && warehouseForm.address && warehouseForm.address.toString().trim()) delete next.address;
            if (next.city && warehouseForm.city && warehouseForm.city.toString().trim()) delete next.city;
            if (next.state && warehouseForm.state && warehouseForm.state.toString().trim()) delete next.state;
            if (next.country && warehouseForm.country && warehouseForm.country.toString().trim()) delete next.country;
            if (next.pincode) {
                const p = warehouseForm.pincode?.toString()?.trim() || '';
                if (p && /^\d{4,6}$/.test(p)) delete next.pincode;
            }

            // lat/lng cleanup
            const lat = warehouseForm.latitude?.toString()?.trim();
            const lng = warehouseForm.longitude?.toString()?.trim();
            if ((next.latitude || next.longitude) && lat && lng) {
                let latOk = !isNaN(Number(lat)) && Number(lat) >= -90 && Number(lat) <= 90;
                let lngOk = !isNaN(Number(lng)) && Number(lng) >= -180 && Number(lng) <= 180;
                if (latOk) delete next.latitude;
                if (lngOk) delete next.longitude;
                if (latOk && lngOk) {
                    delete next.latitude;
                    delete next.longitude;
                }
            }

            if (next.status && warehouseForm.status && ['active', 'inactive'].includes(warehouseForm.status)) delete next.status;

            return next;
        });
    }, [warehouseForm]);

    const handleSubmit = async (e) => {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();

        // validate first
        if (!validateForm()) {
            // do not proceed if validation fails
            return;
        }

        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const payload = {
                name: warehouseForm.name,
                address: warehouseForm.address,
                city: warehouseForm.city,
                state: warehouseForm.state,
                country: warehouseForm.country,
                pincode: warehouseForm.pincode,
                latitude: warehouseForm.latitude,
                longitude: warehouseForm.longitude,
                contactPerson: warehouseForm.contactPerson,
                phoneNumber: warehouseForm.phoneNumber,
                email: warehouseForm.email,
                status: warehouseForm.status
            };
            if (isEditModeRef.current && editWarehouseIdRef.current) {
                await putData(`warehouse/update/${editWarehouseIdRef.current}`, payload, token);
            } else {
                await postData(WAREHOUSE_ROUTES.ADD_WAREHOUSE, payload, token, true);
            }
            handleHideCreateForm();
            setReloadKey((k) => k + 1);
        } catch (error) {
            console.error('Error submitting warehouse:', error);
        }
    };

    const handleFormModeChange = () => {
        setFormMode(prevMode => prevMode === 'view' ? 'edit' : 'view');
    };

    // Add fetchAddressFromLatLng function for reverse geocoding
    const fetchAddressFromLatLng = async (lat, lng) => {
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`
            );
            const data = await response.json();
            const address = data.address || {};
            return {
                address: data.display_name || '',
                city: address.city || address.town || address.village || address.hamlet || '',
                state: address.state || '',
                country: address.country || '',
                pincode: address.postcode || ''
            };
        } catch (error) {
            console.error('Error fetching address:', error);
            return {
                address: '',
                city: '',
                state: '',
                country: '',
                pincode: ''
            };
        }
    };

    useEffect(() => {
        const fetchWarehouses = async (page = 1) => {
            setLoading(true);
            try {
                const tokenValue = sessionStorage.getItem('token');
                const token = tokenValue ? `Bearer ${tokenValue}` : '';
                const params = { page, limit: PAGE_SIZE };
                if (search) params.search = search;
                if (status) params.status = status;
                const data = await getData('warehouse/list', params, token, false);
                setWarehouses(data?.warehouse || []);
                if (data?.stats) setStats(data.stats);
                const meta = data?.meta || { page: 1, totalPages: 1, total: 0 };
                setMeta({
                    page: meta.page,
                    totalPages: meta.totalPages,
                    total: meta.total
                });
            } catch (error) {
                setWarehouses([]);
                setMeta({ page: 1, totalPages: 1, total: 0 });
            } finally {
                setLoading(false);
            }
        };
        fetchWarehouses(meta.page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meta.page, search, status, reloadKey]);

    const renderField = (name, label, opts = {}) => {
        const { select, options, multiline, type, readOnly, display } = opts;
        if (formMode === 'view') {
            const shown = display ?? warehouseForm[name];
            return (
                <Box>
                    <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
                    <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: 13.5, fontWeight: 600, color: '#0f172a', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{shown === '' || shown === null || shown === undefined ? '-' : shown}</Typography>
                </Box>
            );
        }
        return (
            <TextField
                fullWidth
                select={!!select}
                label={label}
                name={name}
                type={type}
                value={warehouseForm[name]}
                onChange={handleChange}
                required={!readOnly}
                multiline={!!multiline}
                rows={multiline ? 2 : undefined}
                variant="outlined"
                size="small"
                InputProps={readOnly ? { readOnly: true } : undefined}
                error={!!errors[name]}
                helperText={errors[name]}
            >
                {select && options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
        );
    };

    return (
        <Box p={3}>
            {!showCreateForm ? (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                        <Box>
                            <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800, fontSize: 25, color: '#0f172a' }}>Warehouses</Typography>
                            <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: 12.5, color: '#64748b' }}>Manage the locations your orders are packed and shipped from.</Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<FaWarehouse />}
                            onClick={handleShowCreateForm}
                            sx={{ fontFamily: 'Mulish, sans-serif', background: 'rgb(255 209 2)', color: 'black', textTransform: 'none', borderRadius: '10px', fontWeight: 700, px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', border: '1px solid black', '&:hover': { background: 'rgb(255 209 2)', transform: 'translateY(-1px)' } }}
                        >
                            Create Warehouse
                        </Button>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2, mb: 3 }}>
                        {[
                            { label: 'Total warehouses', value: stats.total, icon: FaWarehouse, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', key: '' },
                            { label: 'Active', value: stats.active, icon: MdCheckCircle, gradient: 'linear-gradient(135deg,#10b981,#059669)', key: 'active' },
                            { label: 'Inactive', value: stats.inactive, icon: MdPauseCircle, gradient: 'linear-gradient(135deg,#64748b,#475569)', key: 'inactive' }
                        ].map((c) => (
                            <Paper
                                key={c.label}
                                elevation={0}
                                onClick={() => changeStatus(c.key)}
                                sx={{ p: 2.5, borderRadius: '16px', color: '#fff', background: c.gradient, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', outline: status === c.key ? '3px solid #0f172a' : 'none', outlineOffset: 2, boxShadow: '0 4px 12px rgba(15,23,42,.1)', transition: 'transform .15s', '&:hover': { transform: 'translateY(-2px)' } }}
                            >
                                <Box sx={{ background: 'rgba(255,255,255,.22)', borderRadius: '14px', p: 1.5, display: 'flex' }}><c.icon size={26} /></Box>
                                <Box>
                                    <Typography sx={{ fontSize: 11.5, fontWeight: 700, opacity: 0.92, fontFamily: 'Mulish, sans-serif' }}>{c.label}</Typography>
                                    <Typography sx={{ fontSize: 25, fontWeight: 800, lineHeight: 1.2, fontFamily: 'Mulish, sans-serif' }}>{c.value}</Typography>
                                </Box>
                            </Paper>
                        ))}
                    </Box>

                    <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '14px', border: '1px solid #e2e8f0', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                            size="small"
                            placeholder="Search name, city, contact, phone, email, pincode"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            sx={{ flex: 1, minWidth: 260 }}
                            InputProps={{ startAdornment: <MdSearch size={20} style={{ marginRight: 8, color: '#94a3b8' }} /> }}
                        />
                        <TextField select size="small" label="Status" value={status} onChange={(e) => changeStatus(e.target.value)} sx={{ minWidth: 150 }}>
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="inactive">Inactive</MenuItem>
                        </TextField>
                        {(search || status) && (
                            <Button onClick={() => { setSearchInput(''); changeStatus(''); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Clear</Button>
                        )}
                    </Paper>

                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(15,23,42,.05)' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ '& th': { backgroundColor: '#f8fafc', fontWeight: 800, py: 1.75, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', fontFamily: 'Mulish, sans-serif', borderBottom: '2px solid #e2e8f0' } }}>
                                    <TableCell>Warehouse</TableCell>
                                    <TableCell>Address</TableCell>
                                    <TableCell>Contact</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    Array.from({ length: 5 }).map((_, idx) => (
                                        <TableRow key={idx}>
                                            {[200, 240, 180, 80, 80].map((w, i) => <TableCell key={i}><Skeleton width={w} /></TableCell>)}
                                        </TableRow>
                                    ))
                                ) : warehouses.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} sx={{ py: 6, textAlign: 'center' }}>
                                            <Typography sx={{ fontFamily: 'Mulish, sans-serif', color: '#64748b', fontWeight: 600 }}>
                                                {search || status ? 'No warehouses match your filters.' : 'No warehouses yet. Create your first one.'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    warehouses.map((row) => (
                                        <TableRow key={row._id || row.id} hover onClick={() => handleEditWarehouse(row)} sx={{ cursor: 'pointer', '& td': { borderBottom: '1px solid #f1f5f9', py: 1.75, fontFamily: 'Mulish, sans-serif' } }}>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Avatar sx={{ bgcolor: row.status === 'active' ? '#dcfce7' : '#f1f5f9', color: row.status === 'active' ? '#166534' : '#64748b', width: 42, height: 42 }}><FaWarehouse size={18} /></Avatar>
                                                    <Typography sx={{ fontWeight: 800, fontSize: 13.5, fontFamily: 'Mulish, sans-serif', color: '#0f172a' }}>{row.name}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 280 }}>
                                                <Typography sx={{ fontWeight: 700, fontSize: 12.5, fontFamily: 'Mulish, sans-serif' }}>{[row.city, row.state].filter(Boolean).join(', ') || '-'}</Typography>
                                                <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: 'Mulish, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.address}>
                                                    {row.pincode ? `${row.pincode} · ` : ''}{row.address}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography sx={{ fontWeight: 700, fontSize: 12.5, fontFamily: 'Mulish, sans-serif' }}>{row.contactPerson}</Typography>
                                                <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: 'Mulish, sans-serif' }}>{row.phoneNumber}</Typography>
                                                <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: 'Mulish, sans-serif' }}>{row.email}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.status === 'active' ? 'Active' : 'Inactive'}
                                                    size="small"
                                                    sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 700, fontSize: 11, backgroundColor: row.status === 'active' ? '#dcfce7' : '#f1f5f9', color: row.status === 'active' ? '#166534' : '#475569' }}
                                                />
                                            </TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                <IconButton size="small" aria-label="view" onClick={(e) => { e.stopPropagation(); handleEditWarehouse(row); }} sx={{ color: '#4f46e5', backgroundColor: 'rgba(79,70,229,.1)', borderRadius: '8px', mr: 1, '&:hover': { backgroundColor: 'rgba(79,70,229,.2)' } }}>
                                                    <FaEye size={16} />
                                                </IconButton>
                                                <IconButton size="small" aria-label="delete" onClick={(e) => handleDeleteClick(row, e)} sx={{ color: '#dc2626', backgroundColor: 'rgba(220,38,38,.1)', borderRadius: '8px', '&:hover': { backgroundColor: 'rgba(220,38,38,.2)' } }}>
                                                    <MdDeleteForever size={20} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Pagination
                            count={meta.totalPages}
                            page={meta.page}
                            onChange={handlePageChange}
                            sx={{ '& .MuiPagination-ul': { flexWrap: 'nowrap' }, '& .MuiPaginationItem-root': { fontFamily: 'Mulish, sans-serif', fontWeight: 600, borderRadius: '8px', '&.Mui-selected': { backgroundColor: 'rgb(255, 209, 2)', color: 'black', '&:hover': { backgroundColor: 'rgb(255, 190, 0)' } } } }}
                            shape="rounded"
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                    {meta.total > 0 && (
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                            Showing {(meta.page - 1) * PAGE_SIZE + 1} to {Math.min(meta.page * PAGE_SIZE, meta.total)} of {meta.total} entries
                        </Typography>
                    )}
                </>
            ) : (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <IconButton onClick={handleHideCreateForm} aria-label="back" sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
                                <MdArrowBack size={20} />
                            </IconButton>
                            <Box>
                                <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800, fontSize: 23.5, color: '#0f172a', lineHeight: 1.2 }}>
                                    {!isEditModeRef.current ? 'Create Warehouse' : formMode === 'edit' ? 'Edit Warehouse' : 'View Warehouse'}
                                </Typography>
                                <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: 12.5, color: '#64748b' }}>
                                    {!isEditModeRef.current
                                        ? 'Add a new location to pack and ship orders from.'
                                        : warehouseForm.name || 'Warehouse details'}
                                </Typography>
                            </Box>
                            {isEditModeRef.current && (
                                <Chip
                                    label={warehouseForm.status === 'active' ? 'Active' : 'Inactive'}
                                    size="small"
                                    sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 700, backgroundColor: warehouseForm.status === 'active' ? '#dcfce7' : '#f1f5f9', color: warehouseForm.status === 'active' ? '#166534' : '#475569' }}
                                />
                            )}
                        </Box>
                        {isEditModeRef.current && (
                            <Button
                                onClick={handleFormModeChange}
                                startIcon={formMode === 'view' ? <FaEdit /> : <FaEye />}
                                sx={{ fontFamily: 'Mulish, sans-serif', textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 2.5, color: '#0f172a', background: formMode === 'view' ? 'rgb(255 209 2)' : '#e2e8f0', '&:hover': { background: formMode === 'view' ? 'rgb(255 190 0)' : '#cbd5e1' } }}
                            >
                                {formMode === 'view' ? 'Edit warehouse' : 'Switch to view'}
                            </Button>
                        )}
                    </Box>

                    <form onSubmit={handleSubmit} id="warehouse-form">
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <FormCard title="Basic information" subtitle="Name and availability of this warehouse" accent="#6366f1" icon={FaWarehouse}>
                                <Grid container spacing={2.5}>
                                    <Grid item xs={12} md={8}>{renderField('name', 'Warehouse name')}</Grid>
                                    <Grid item xs={12} md={4}>
                                        {renderField('status', 'Status', {
                                            select: true,
                                            options: [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }],
                                            display: warehouseForm.status === 'active' ? 'Active' : 'Inactive'
                                        })}
                                    </Grid>
                                </Grid>
                            </FormCard>

                            <FormCard title="Contact details" subtitle="Who to reach for pickups and stock queries" accent="#ec4899" icon={MdPerson}>
                                <Grid container spacing={2.5}>
                                    <Grid item xs={12} md={4}>{renderField('contactPerson', 'Contact person')}</Grid>
                                    <Grid item xs={12} md={4}>{renderField('phoneNumber', 'Phone number')}</Grid>
                                    <Grid item xs={12} md={4}>{renderField('email', 'Email', { type: 'email' })}</Grid>
                                </Grid>
                            </FormCard>

                            <FormCard title="Location" subtitle={formMode === 'edit' ? 'Click on the map to pick the spot. The address below fills in automatically.' : 'Where this warehouse is located'} accent="#0ea5e9" icon={MdPlace}>
                                <Box sx={{ mb: 2.5, borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0', pointerEvents: formMode === 'edit' ? 'auto' : 'none' }}>
                                    <MapPicker
                                        lat={warehouseForm.latitude}
                                        lng={warehouseForm.longitude}
                                        onChange={async ({ lat, lng }) => {
                                            if (formMode !== 'edit') return;
                                            const addr = await fetchAddressFromLatLng(lat, lng);
                                            setWarehouseForm(prev => ({
                                                ...prev,
                                                latitude: lat,
                                                longitude: lng,
                                                address: addr.address,
                                                city: addr.city,
                                                state: addr.state,
                                                country: addr.country,
                                                pincode: addr.pincode
                                            }));
                                        }}
                                    />
                                </Box>
                                <Grid container spacing={2.5}>
                                    <Grid item xs={12}>{renderField('address', 'Address', { multiline: true })}</Grid>
                                    <Grid item xs={12} sm={6} md={3}>{renderField('city', 'City')}</Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        {renderField('state', 'State', { select: true, options: indianStates.map((st) => ({ value: st, label: st })) })}
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>{renderField('country', 'Country')}</Grid>
                                    <Grid item xs={12} sm={6} md={3}>{renderField('pincode', 'Pincode')}</Grid>
                                    <Grid item xs={12} sm={6}>{renderField('latitude', 'Latitude', { readOnly: true })}</Grid>
                                    <Grid item xs={12} sm={6}>{renderField('longitude', 'Longitude', { readOnly: true })}</Grid>
                                </Grid>
                            </FormCard>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                <Button
                                    onClick={handleHideCreateForm}
                                    variant="outlined"
                                    sx={{ fontFamily: 'Mulish, sans-serif', textTransform: 'none', fontWeight: 700, px: 3, py: 1, borderRadius: '10px', minWidth: 120, color: '#475569', borderColor: '#cbd5e1', '&:hover': { borderColor: '#94a3b8', backgroundColor: '#f1f5f9' } }}
                                >
                                    {formMode === 'edit' ? 'Cancel' : 'Close'}
                                </Button>
                                {formMode === 'edit' && (
                                    <Button
                                        type="submit"
                                        form="warehouse-form"
                                        variant="contained"
                                        sx={{ fontFamily: 'Mulish, sans-serif', textTransform: 'none', bgcolor: 'rgb(255 209 2)', color: 'black', px: 3, py: 1, borderRadius: '10px', minWidth: 180, fontWeight: 800, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { bgcolor: 'rgb(255 190 0)' } }}
                                    >
                                        {isEditModeRef.current ? 'Update warehouse' : 'Create warehouse'}
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </form>
                </>
            )}

            <Dialog
                open={deleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                PaperProps={{
                    style: {
                        borderRadius: '10px',
                        padding: '20px',
                        minWidth: '400px'
                    }
                }}
            >
                <DialogTitle sx={{ fontSize: '18px', fontWeight: 'bold', textAlign: 'center' }}>
                    <img style={{ width: '80px' }} src='./img/del1.png' alt='delete' />
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ textAlign: 'center', fontSize: '14.5px', mb: 1 }}>
                        {warehouseToDelete && (
                            <>Are you sure you want to delete warehouse <b>{warehouseToDelete.name}</b>?</>
                        )}
                    </DialogContentText>
                    <Box sx={{
                        textAlign: 'center',
                        backgroundColor: '#fff3cd',
                        padding: '12px',
                        borderRadius: '4px',
                        borderLeft: '4px solid #ffc107',
                        marginBottom: '2px'
                    }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#856404', marginBottom: '3px' }}>
                            Warning
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#856404' }}>
                            By deleting, you won't be able to access it again.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: '20px', padding: '20px' }}>
                    <Button
                        onClick={handleCloseDeleteDialog}
                        variant="outlined"
                        sx={{
                            minWidth: '120px',
                            borderColor: 'gray',
                            color: 'gray',
                            '&:hover': {
                                borderColor: 'darkgray',
                                backgroundColor: 'rgba(0,0,0,0.05)'
                            }
                        }}
                    >
                        No, Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        sx={{
                            minWidth: '120px',
                            backgroundColor: 'red',
                            '&:hover': {
                                backgroundColor: '#bb2d3b'
                            }
                        }}
                        autoFocus
                    >
                        Yes, Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Warehouse;
